'use client';

import { ProfileType } from 'data/interfaces/IRegistration';
import { IUser, IUserProfile } from 'data/interfaces/IUser';
import UserRepository from 'data/repositories/UserRepository';
import Cookies from 'js-cookie';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Subject } from 'rxjs';
import { CookiesLifeTime } from 'types/constants';
import { CookiesType, SnackbarType } from 'types/enums';
import { useAppContext } from './state';

export interface UserContextState {
  token: string;
  setToken: (token: string) => Promise<boolean>;
  isLogged: boolean;
  isAuth: MutableRefObject<boolean>;
  logout: (noReplace?: boolean) => void;
  userLoaded: boolean;
  user: IUser;
  currentProfile: IUserProfile;
  profileType: ProfileType | undefined;
  setProfileType: (t: ProfileType) => void;
  editCurrentProfile: (d: Partial<IUserProfile>) => Promise<IUserProfile | false>;
  removeCurrentProfile: () => void;
  updateUser: (user?: IUser, disableNotify?: boolean) => Promise<IUser>;
  refreshUser: () => void;
}

const loginState$ = new Subject<boolean>();
const loginUserState$ = new Subject<IUser | null>();

const defaultValue: Partial<UserContextState> = {};

const UserContext = createContext<Partial<UserContextState>>(defaultValue);

interface Props {
  children: React.ReactNode;
  token?: string;
  user?: IUser;
  profileType?: ProfileType;
}

export function UserWrapper(props: Props) {
  const t = useTranslations();
  const appContext = useAppContext();
  const router = useRouter();

  const [token, setUserToken] = useState<string | null>(props.token ?? null);
  const [user, setUser] = useState<IUser | null>(props.user);
  const [userLoaded, setUserLoaded] = useState<boolean>(false);
  const [profileType, setProfileTypeState] = useState<ProfileType>(
    props.profileType || ProfileType.Employee,
  );
  const [isLogged, setIsLogged] = useState<boolean>(props.user?.isRegistrationCompleted || false);
  const [currentProfile, setCurrentProfileState] = useState<IUserProfile>(
    props.user?.profiles ? props.user.profiles[0] : null,
  );
  const pathname = usePathname();

  const userRef = useRef<IUser | null>(null);
  const isAuth = useRef<boolean>(false);

  const setProfileType = (type: ProfileType) => {
    Cookies.remove(CookiesType.accessToken);
    Cookies.remove(CookiesType.profileType);
    Cookies.set(CookiesType.profileType, type);
    setProfileTypeState(type);
  };

  const updateUser = useCallback(
    async (newUser?: IUser, disableNotify?: boolean): Promise<IUser> => {
      if (newUser) {
        UserRepository.editCurrentUser(user.id, newUser).then((usr) => {
          setUser(usr);
          return usr;
        });
      } else {
        const data = await UserRepository.getCurrentUser();
        if (data) {
          setUser(data);

          setCurrentProfileState(data.profiles[0]);
          if (
            data.profiles.length === 0 &&
            data.isRegistrationCompleted &&
            !disableNotify &&
            pathname !== '/registration'
          ) {
            appContext.showSnackbar(t('registration_accountNotExist'), SnackbarType.error);
            router.push('/registration');
          }
        }
        setUserLoaded(true);
        return data;
      }
    },
    [appContext, router, t],
  );

  const setToken = async (token: string): Promise<boolean> => {
    const newToken = token;
    const oldToken = Cookies.get(CookiesType.accessToken) ?? null;
    Cookies.set(CookiesType.accessToken, token, {
      expires: CookiesLifeTime.accessToken,
    });
    Cookies.set(CookiesType.fromLogout, 'false');

    setUserToken(newToken);
    if (!oldToken && newToken) {
      loginState$.next(true);
      await updateUser();
      loginUserState$.next(user);
    }
    if (oldToken && !newToken) {
      loginState$.next(false);
      loginUserState$.next(null);
    }
    setIsLogged(true);
    return true;
  };

  const editCurrentProfile = async (data: Partial<IUserProfile>): Promise<IUserProfile | false> => {
    return UserRepository.editProfileById(data, currentProfile?.id)
      .then((p) => {
        if (p) {
          setCurrentProfileState(p);
          return p;
        } else {
          return false;
        }
      })
      .catch((err) => {
        console.warn(err);
        return false;
      });
  };

  const logout = (noReplace?: boolean) => {
    if (!noReplace) router.replace('/');
    Cookies.remove(CookiesType.refreshToken);
    Cookies.remove(CookiesType.accessToken);
    Cookies.remove(CookiesType.profileType);
    Cookies.remove(CookiesType.uuid);
    Cookies.set(CookiesType.fromLogout, 'true');
    setUserToken(null);
    setIsLogged(false);
    setCurrentProfileState(null);
    setUser(null);
    loginState$.next(false);
  };

  const removeCurrentProfile = () => {
    if (confirm(t('profile_confirmRemoveAccount'))) {
      UserRepository.removeCurrentUserById(currentProfile?.id).then(() => {
        logout();
        router.replace('/registration');
      });
    }
  };

  const refreshUser = () => {
    UserRepository.getCurrentUser().then((res) => {
      if (res) {
        setUser(res);
      }
    });
  };

  useEffect(() => {
    if (user && userLoaded && !user.email) {
      router.push('/registration');
    }
  }, [router, user, userLoaded]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (props.token && user && props.user?.isRegistrationCompleted) {
      setIsLogged(true);
      if (!currentProfile) {
        setCurrentProfileState(props.user.profiles[0]);
      }
      isAuth.current = true;
    }
  }, [user, props.user, props.token, currentProfile]);

  useEffect(() => {
    if (currentProfile) {
      Cookies.set(CookiesType.profileType, currentProfile?.profileType);
    }
  }, [currentProfile]);

  useEffect(() => {
    if (props.token !== Cookies.get(CookiesType.accessToken) && !user) {
      updateUser()
        .then(() => {
          setUserLoaded(true);
        })
        .catch(() => {
          setIsLogged(false);
        });
    }
  }, [props.token]);

  const value: UserContextState = {
    token,
    setToken,
    isLogged,
    userLoaded,
    user,
    profileType,
    setProfileType,
    currentProfile,
    isAuth,
    logout,
    editCurrentProfile,
    removeCurrentProfile,
    updateUser,
    refreshUser,
  };

  return <UserContext.Provider value={value}>{props.children}</UserContext.Provider>;
}

export function useUserContext() {
  return useContext(UserContext);
}
